import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton } from '@headlessui/react';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';
import { Option } from '~/types';
import { areStringArraysEqual } from '../../helpers/arrayUtils';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useScrollTrigger } from '../../hooks/useScrollTrigger';
import { Combolist } from '../Combolist';
import { PopoverStateChangedEffect } from '../PopoverStateChangedEffect';
import { Pill } from './nucleus/Pill';

type Props = {
  label: string;
  options: Option[];
  multiple?: boolean;
  value: string[];
  onChange: (newValue: string[]) => void;
  searchable?: boolean;
  showLabel?: 'always' | 'auto';
  selectAll?: boolean;
  navScrollDetectionEnabled?: boolean; // Use same logic as nav
};

export function FilterSelect({
  label,
  options,
  multiple = true,
  value,
  onChange,
  searchable,
  showLabel = 'auto',
  selectAll = multiple ? true : false,
  navScrollDetectionEnabled = true,
}: Props) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [selectedItems, setSelectedItems] = useState<string[]>(value);
  const [committedValueForPill, setCommittedValueForPill] =
    useState<string[]>(value);
  const [initialValueOnOpen, setInitialValueOnOpen] = useState<string[]>(value);
  const [cancelling, setCancelling] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed', // Use fixed positioning for better stability on mobile
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'flip',
        options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
          boundary: 'clippingParents', // More stable boundary detection
          altBoundary: false,
          rootBoundary: 'viewport',
          tether: false, // Prevent popper from moving when reference moves
        },
      },
      {
        name: 'computeStyles',
        options: {
          adaptive: true,
          gpuAcceleration: true, // Use GPU acceleration for smoother performance
        },
      },
      {
        name: 'eventListeners',
        options: {
          scroll: false, // Disable scroll tracking to keep popper stable once positioned
          resize: true, // Keep resize tracking for window resize events
        },
      },
    ],
  });

  // Use the same scroll threshold as NavContainer for consistency
  const MOBILE_SCROLL_THRESHOLD = 150;

  // Mirror the exact same logic as NavContainer to detect when nav is hidden
  // But only when scroll detection is enabled (same as nav)
  const isNavHidden = useScrollTrigger({
    threshold: MOBILE_SCROLL_THRESHOLD,
    enabled: isMobile && navScrollDetectionEnabled,
  });

  // Track popover open state and close handler for auto-close effect
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const closePopoverRef = useRef<(() => void) | null>(null);
  const renderPropOpenRef = useRef<boolean>(false);

  // Auto-close on mobile when nav is hidden
  useEffect(() => {
    if (!isMobile || !popoverOpen || !isNavHidden) {
      return;
    }

    closePopoverRef.current?.();
  }, [isMobile, popoverOpen, isNavHidden]);

  // Sync Headless UI open value to local state
  // This effect ensures popoverOpen stays in sync with the render-prop open value
  // We use renderPropOpenRef which is set inside the render prop to avoid state updates during render
  // useLayoutEffect runs synchronously after DOM mutations, ensuring we sync after every render
  useLayoutEffect(() => {
    if (renderPropOpenRef.current !== popoverOpen) {
      setPopoverOpen(renderPropOpenRef.current);
    }
  });

  // ! Combolist does not yet support a custom identifier so map options to identifiable items
  const items = options.map((opt) => ({
    $group: opt.$group,
    id: String(opt.value),
    name: opt.label,
  }));

  // There was an issue where the filter would reset its state when hovering over a site in SiteSelect but I can't reproduce it. Needs to be thouroughly tested

  // Effect to sync internal states if 'value' prop changes from outside
  useEffect(() => {
    // Helper to compare string arrays. Sorts copies to ensure order doesn't matter.
    // const arraysHaveSameStrings = (arrA: string[], arrB: string[]) => {
    //   if (arrA.length !== arrB.length) return false;
    //   const sortedA = [...arrA].sort();
    //   const sortedB = [...arrB].sort();
    //   for (let i = 0; i < sortedA.length; i++) {
    //     if (sortedA[i] !== sortedB[i]) return false;
    //   }
    //   return true;
    // };

    // Only update internal state if the incoming 'value' prop is meaningfully different.
    // This helps prevent loops if 'value' is a new array instance with the same content.
    if (!areStringArraysEqual(selectedItems, value)) {
      setSelectedItems(value);
    }
    if (!areStringArraysEqual(committedValueForPill, value)) {
      setCommittedValueForPill(value);
    }
    // initialValueOnOpen is intentionally set only when the popover opens via direct interaction,
    // not automatically synced with the 'value' prop in this effect.
  }, [value, selectedItems, committedValueForPill]);

  const truncatedLabel = (label: string) => {
    if (showLabel === 'auto' && label.length > 20) {
      return label.slice(0, 17) + '...';
    }
    return label;
  };

  const handleClose = () => {
    if (cancelling) {
      setCancelling(false);
      // If cancelling, revert internal selection and call onChange to revert parent state.
      setSelectedItems(initialValueOnOpen);
      onChange(initialValueOnOpen);
      setCommittedValueForPill(initialValueOnOpen);
      return;
    }
    // OK path or closing by clicking outside (if cancelling is false)
    // onChange(selectedItems); // This will have been called incrementally in multi-select
    // setCommittedValueForPill(selectedItems); // Also called incrementally
  };

  return (
    <Popover className='relative'>
      {({ open, close: closePopover }) => {
        // Capture latest close handler for effects
        closePopoverRef.current = closePopover;
        // Store render-prop open value in ref so useEffect can sync it to state
        renderPropOpenRef.current = open;

        const internalOnClose = () => {
          if (!open) {
            handleClose();
          }
        };

        const getLabel = () => {
          const data = open ? selectedItems : committedValueForPill;
          return truncatedLabel(
            data.length === 0
              ? label
              : data.length === 1
              ? (showLabel === 'always' ? label + ' • ' : '') +
                (options.find((option) => String(option.value) === data[0])
                  ?.label || data[0])
              : `${label} • ${
                  data.length >= items.length ? 'All' : data.length
                }`
          );
        };

        return (
          <>
            <PopoverButton
              ref={setReferenceElement}
              className='mx-[3px] my-1 rounded-full'
              onClick={(event) => {
                if (options.length === 1) {
                  event.preventDefault();
                  const newValue = value.includes(options[0].value)
                    ? []
                    : [String(options[0].value)];
                  onChange(newValue);
                } else {
                  setInitialValueOnOpen(value);
                  setSelectedItems(value);
                  setCancelling(false);
                }
              }}
            >
              <Pill
                type='filter'
                hasParent
                active={
                  (open ? selectedItems : committedValueForPill).length > 0
                }
              >
                {getLabel()}
                {(open ? selectedItems : committedValueForPill).length > 0 && (
                  <span
                    className='ml-2 max-h-full cursor-pointer pt-0.5'
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange([]);
                      setSelectedItems([]); // This line was previously part of a HACK related to state reset.
                      setCommittedValueForPill([]);
                      closePopover();
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                )}
              </Pill>
            </PopoverButton>

            {open &&
              ReactDOM.createPortal(
                <div
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                  className={classNames(
                    'prevent-drag-scroll border-gray-10 z-[100] w-72 rounded-md border bg-white shadow-lg focus:outline-none',
                    {
                      'pt-1.5': !searchable,
                    }
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Combolist
                    items={items}
                    searchKeys={['name']}
                    placeholder={label.toLowerCase()}
                    multiple={multiple}
                    value={items.filter((item) =>
                      selectedItems.includes(item.id)
                    )}
                    onChange={(selected) => {
                      const newSelectedIds = selected
                        .filter((item) => !!item)
                        .map(({ id }) => id);
                      setSelectedItems(newSelectedIds);

                      if (!multiple) {
                        onChange(newSelectedIds);
                        setCommittedValueForPill(newSelectedIds); // Commit for single select immediate change
                        closePopover();
                      } else {
                        // For multi-select, also call onChange and update committedValue immediately
                        onChange(newSelectedIds);
                        setCommittedValueForPill(newSelectedIds);
                      }
                    }}
                    renderInner={(item) => item.name}
                    searchable={searchable}
                    selectAll={selectAll}
                  />
                  {multiple && (
                    <div className='flex border-t p-1.5 text-xs'>
                      <button
                        className='flex-grow p-2 text-left uppercase text-gray-600 hover:text-black'
                        onClick={() => {
                          setCancelling(true); // Set cancelling flag
                          // handleClose will now correctly revert to initialValueOnOpen and call onChange
                          closePopover();
                        }}
                      >
                        {t('cancel')}
                      </button>
                      <button
                        className='text-blue-600 hover:text-blue-800 flex-grow p-2 text-right uppercase'
                        onClick={() => {
                          setCancelling(false);
                          closePopover();
                        }}
                      >
                        {t('ok')}
                      </button>
                    </div>
                  )}
                </div>,
                document.body
              )}

            <PopoverStateChangedEffect open={open} onClose={internalOnClose} />
          </>
        );
      }}
    </Popover>
  );
}
